// Writes for API keys and Claude's grants (Account, the admin user drawer).
//
// A new key's secret comes back as the create mutation's result, which the sheet reads from
// `mutateAsync` and holds only while it is open. The mutation keeps no closure that could
// reach the sheet's state (its variables are the plain request), is collected the moment
// nothing observes it (`gcTime: 0`), and the sheet resets it on close, so the secret does
// not outlive the sheet in TanStack's mutation cache. Nothing here logs a body.

import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useRegisteredMutation } from './mutation';
import { adminAudit, adminAuditRecent, adminUserCredentials, myCredentials } from './resources';
import type { CreateApiKeyRequest, CreateApiKeyResponse, CredentialSummary } from './types';

function invalidateCredentials(qc: QueryClient) {
  for (const queryKey of [myCredentials.all, adminUserCredentials.all, adminAudit.all, adminAuditRecent.all]) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** `POST /me/api-keys`: resolves to the key and its secret, for the sheet to show once. */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    // Gone from the mutation cache as soon as the sheet stops observing it (or resets it).
    gcTime: 0,
    mutationFn: (request: CreateApiKeyRequest) =>
      api.post<CreateApiKeyResponse>('/me/api-keys', { body: request }),
    onSuccess: () => invalidateCredentials(qc),
  });
}

/** `POST /me/credentials/{id}/revoke`: one of the caller's own keys or grants. */
export function useRevokeMyCredential() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: (credentialId: string) =>
      api.post<CredentialSummary>(`/me/credentials/${encodeURIComponent(credentialId)}/revoke`),
    onSuccess: () => invalidateCredentials(qc),
  });
}

/** `POST /admin/users/{id}/credentials/{credential_id}/revoke`: an unscoped admin's. */
export function useAdminRevokeCredential() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: ({ userId, credentialId }: { userId: string; credentialId: string }) =>
      api.post<CredentialSummary>(
        `/admin/users/${encodeURIComponent(userId)}/credentials/${encodeURIComponent(credentialId)}/revoke`,
      ),
    onSuccess: () => invalidateCredentials(qc),
  });
}
